import { getFirestoreSdk } from '@/lib/firestoreSdk';
import { useEffect, useState } from 'react';

export interface FirestoreDocResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  fromCache: boolean;
  hasPendingWrites: boolean;
}

/**
 * Subscribes to a single Firestore document and returns its data plus offline
 * metadata. `fromCache` and `hasPendingWrites` are exposed so the UI can show
 * sync affordances (e.g. a "syncing…" indicator on a card with queued writes).
 *
 * Pass `null` as `path` to skip the subscription (useful for conditional reads
 * before an id is available). Optional `segments` are joined into the doc path.
 *
 * The Firestore SDK loads lazily (see @/lib/firestoreSdk) — the subscription
 * is established as soon as the SDK + persistent cache are ready, which for a
 * cold load costs one extra chunk fetch versus the previous eager-init
 * behaviour, and is instant on repeat visits (SW-cached chunk).
 */
export function useFirestoreDoc<T>(
  path: string | null,
  segments?: readonly string[],
): FirestoreDocResult<T> {
  const fullPath = path === null ? null : [path, ...(segments ?? [])].filter(Boolean).join('/');

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(fullPath !== null);
  const [error, setError] = useState<Error | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const [hasPendingWrites, setHasPendingWrites] = useState<boolean>(false);

  useEffect(() => {
    if (!fullPath) {
      setData(null);
      setLoading(false);
      setError(null);
      setFromCache(false);
      setHasPendingWrites(false);
      return;
    }

    // Reset stale data from the previous path so vehicle A's data never
    // flashes while the subscription for vehicle B is establishing.
    setData(null);
    setFromCache(false);
    setHasPendingWrites(false);
    setLoading(true);
    setError(null);

    let cancelled = false;
    let unsub: (() => void) | undefined;

    getFirestoreSdk()
      .then(({ sdk, db }) => {
        if (cancelled) return;
        const ref = sdk.doc(db, fullPath);
        unsub = sdk.onSnapshot(
          ref,
          { includeMetadataChanges: true },
          (snap) => {
            if (cancelled) return;
            setData(snap.exists() ? ({ id: snap.id, ...snap.data() } as T) : null);
            setFromCache(snap.metadata.fromCache);
            setHasPendingWrites(snap.metadata.hasPendingWrites);
            setLoading(false);
          },
          (err) => {
            if (cancelled) return;
            setError(err);
            setLoading(false);
          },
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to load Firestore'));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [fullPath]);

  return { data, loading, error, fromCache, hasPendingWrites };
}
